import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus, Save, BookMarked } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function ListEditor({ label, items, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = () => { const v = val.trim(); if (v && !items.includes(v)) onChange([...items, v]); setVal(""); };
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it} className="inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs text-accent-foreground" data-testid={`${testid}-chip`}>
            {it}<button onClick={() => onChange(items.filter((x) => x !== it))} className="hover:text-red-500"><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={`Add ${label.toLowerCase()}...`} data-testid={`${testid}-input`} />
        <Button type="button" variant="secondary" onClick={add} data-testid={`${testid}-add`}><Plus size={15} /></Button>
      </div>
    </div>
  );
}

const LANDING = [
  { v: "/", l: "Dashboard" }, { v: "/goals", l: "Goals & OKRs" }, { v: "/pipeline", l: "R&D Pipeline" },
  { v: "/benchmarks", l: "Benchmark Products" }, { v: "/services", l: "Services & Feedback" }, { v: "/research", l: "Research Topics" },
];
const RANGES = [["all", "All Time"], ["today", "Today"], ["month", "This Month"], ["quarter", "This Quarter"], ["last_quarter", "Last Quarter"], ["ytd", "Year-to-Date"]];

export default function Settings() {
  const { settings, save } = useSettings();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(settings);
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const saveSettings = async () => {
    setBusy(true);
    try { await save(form); toast.success("Settings saved"); } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const saveProfile = async () => {
    try { const { data } = await api.put("/auth/profile", { name }); setUser(data); toast.success("Profile updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your profile, workspace lists, and preferences.</p>
      </div>
      <Tabs defaultValue="profile">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="workspace" data-testid="settings-tab-workspace">Workspace & Lists</TabsTrigger>
          <TabsTrigger value="prefs" data-testid="settings-tab-prefs">Preferences</TabsTrigger>
          <TabsTrigger value="research" data-testid="settings-tab-research">Research Links</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-5">
          <Card className="max-w-lg space-y-4 border-slate-200 p-6">
            <div><Label>Display Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></div>
            <div><Label>Email</Label><Input value={user?.email || ""} disabled /></div>
            <Button onClick={saveProfile} data-testid="save-profile-btn"><Save size={15} className="mr-1" /> Save Profile</Button>
          </Card>
        </TabsContent>

        <TabsContent value="workspace" className="mt-5">
          <Card className="space-y-6 border-slate-200 p-6">
            <div><Label>Workspace Name</Label><Input value={form.workspace_name} onChange={(e) => upd("workspace_name", e.target.value)} data-testid="workspace-name-input" /></div>
            <ListEditor label="Brand Targets" items={form.brands} onChange={(v) => upd("brands", v)} testid="brands" />
            <ListEditor label="R&D Idea Categories" items={form.idea_categories} onChange={(v) => upd("idea_categories", v)} testid="idea-cats" />
            <ListEditor label="Goal Categories" items={form.goal_categories} onChange={(v) => upd("goal_categories", v)} testid="goal-cats" />
            <ListEditor label="Team Members / Researchers" items={form.researchers} onChange={(v) => upd("researchers", v)} testid="researchers" />
            <ListEditor label="Rejection Reasons" items={form.rejection_reasons} onChange={(v) => upd("rejection_reasons", v)} testid="rejections" />
            <Button onClick={saveSettings} disabled={busy} data-testid="save-settings-btn"><Save size={15} className="mr-1" /> {busy ? "Saving..." : "Save Changes"}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="prefs" className="mt-5">
          <Card className="max-w-lg space-y-4 border-slate-200 p-6">
            <div><Label>Default Date Range</Label>
              <Select value={form.default_date_range} onValueChange={(v) => upd("default_date_range", v)}>
                <SelectTrigger data-testid="default-range-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RANGES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Default Landing Tab</Label>
              <Select value={form.default_landing} onValueChange={(v) => upd("default_landing", v)}>
                <SelectTrigger data-testid="default-landing-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LANDING.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button onClick={saveSettings} disabled={busy} data-testid="save-prefs-btn"><Save size={15} className="mr-1" /> Save Preferences</Button>
          </Card>
        </TabsContent>

        <TabsContent value="research" className="mt-5">
          <Card className="max-w-lg space-y-3 border-slate-200 p-6">
            <div className="flex items-center gap-2 text-slate-700"><BookMarked size={18} className="text-primary" /><h3 className="font-head font-bold">Research Topics & Links</h3></div>
            <p className="text-sm text-muted-foreground">Track the topics you're researching and keep all reference links together in a dedicated space.</p>
            <Link to="/research"><Button variant="secondary" data-testid="go-research-btn">Open Research Topics</Button></Link>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
